import React, { FC, ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import { BaseStyleEnums, IdEnums } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { ContextLabel } from '../../../../enums';
import { StatDivider } from '../../../../styles';
import { ContextDescriptor, LandingGame } from '../../../../types';

import {
    CompareContainer,
    HeaderAndSeparator,
    HeaderContainer,
    HeaderName,
    SubHeader,
    TeamContainer,
    TeamItem,
} from './styles';

type HeaderDetailProps = {
    children: ReactNode;
    message: ReactNode;
};

type Props = {
    message: ReactNode;
    game: LandingGame;
};

export const ContextSubHeader: FC<{ contextDescriptor?: ContextDescriptor; currentSeason?: number }> = ({
    contextDescriptor,
    currentSeason,
}) => {
    switch (contextDescriptor?.contextLabel) {
        case ContextLabel.REGULAR_SEASON:
            return contextDescriptor.contextSeason === currentSeason ? (
                <FormattedMessage {...CommonMessages.gameTypeDescriptions.long[IdEnums.GameType.regularSeason]} />
            ) : (
                <FormattedMessage
                    description="Displays a teams leaders in points, goals, assists for the entire regular season"
                    defaultMessage="{season} Regular Season"
                    values={{
                        season: hockeyFormatter.singleSeasonFormatter(contextDescriptor.contextSeason),
                    }}
                />
            );
        case ContextLabel.TO_DATE:
            return (
                <FormattedMessage
                    description="Displays a teams leaders in points, goals, assists for this season up to this date"
                    defaultMessage="{season} To Date"
                    values={{
                        season: hockeyFormatter.singleSeasonFormatter(contextDescriptor.contextSeason),
                    }}
                />
            );
        case ContextLabel.LAST_5_GAMES:
            return (
                <FormattedMessage
                    description="Displays a teams leaders in points, goals, assists for the last 5 games"
                    defaultMessage="Last 5 Games"
                />
            );
        case ContextLabel.PLAYOFFS:
            return contextDescriptor.contextSeason === currentSeason ? (
                <FormattedMessage {...CommonMessages.gameTypeDescriptions.long[IdEnums.GameType.playoffs]} />
            ) : (
                <FormattedMessage
                    description="Displays a teams leaders in points, goals, assists for the Playoffs"
                    defaultMessage="{season} Playoffs"
                    values={{
                        season: String(contextDescriptor.contextSeason).substring(4),
                    }}
                />
            );
        case ContextLabel.FOUR_NATIONS:
            return <FormattedMessage {...CommonMessages.gameTypeDescriptions.long[IdEnums.GameType.fourNations]} />;
        default:
            return null;
    }
};

export const HeaderDetails: FC<HeaderDetailProps> = ({ children, message }) => (
    <HeaderContainer>
        <HeaderName role="heading" aria-level={2}>
            {message}
        </HeaderName>
        <SubHeader aria-level={3}>{children}</SubHeader>
    </HeaderContainer>
);

const CompareHeader: FC<Props> = ({ message, game }) => {
    const size = useSizeWithThrottle();
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    return (
        <CompareContainer>
            <HeaderAndSeparator>
                <HeaderDetails message={message}>
                    <ContextSubHeader contextDescriptor={game?.matchup?.skaterComparison} currentSeason={game.season} />
                </HeaderDetails>
                {isMedium && <StatDivider />}
            </HeaderAndSeparator>

            <TeamContainer>
                <TeamItem>
                    <TeamDisplay
                        logoUrlOverride={game.awayTeam.logo}
                        team={{ triCode: game.awayTeam.abbrev }}
                        displayName={false}
                    />
                </TeamItem>
                <TeamItem $isHome>
                    <TeamDisplay
                        logoUrlOverride={game.homeTeam.logo}
                        team={{ triCode: game.homeTeam.abbrev }}
                        displayName={false}
                    />
                </TeamItem>
            </TeamContainer>
        </CompareContainer>
    );
};

export default CompareHeader;
